/**
 * ScutaEntries — Activity-sourced SCUTA timeline.
 *
 * Archival, chronological, observational. Not a feed.
 * No reactions. No comments. No engagement mechanics.
 *
 * Feels like: record, timeline, memory, continuity.
 * Not: posting, sharing, reacting.
 *
 * COLOR PALETTE (SCUTA-locked):
 * - Background: #0B0F14
 * - Primary Text: #E6E9EE
 * - Secondary Text: #9AA3AF
 * - Accent: #C2A45E (Oxidized Gold)
 * - Dividers: #1E2936
 */

import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card } from '../components/ui/card';
import {
  ArrowLeft, Clock, MapPin, User, Building, Users, Landmark,
  FileText, Image, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: { label: 'Draft', color: '#9AA3AF' },
  pending: { label: 'Pending Review', color: '#C2A45E' },
  approved: { label: 'Published', color: '#7A8F5B' },
  rejected: { label: 'Rejected', color: '#9AA3AF' },
  archived: { label: 'Archived', color: '#9AA3AF' },
};

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function groupByMonth(entries) {
  const groups = {};
  for (const entry of entries) {
    const date = new Date(entry.completion_date || entry.created_at);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    if (!groups[key]) groups[key] = { key, label, entries: [] };
    groups[key].entries.push(entry);
  }
  return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
}

export default function ScutaEntries() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('approved');

  useEffect(() => {
    loadEntries();
  }, [filter]);

  // Force dark background on body for SCUTA pages
  useEffect(() => {
    const originalBg = document.body.style.backgroundColor;
    document.body.style.backgroundColor = '#0B0F14';
    return () => {
      document.body.style.backgroundColor = originalBg;
    };
  }, []);

  const loadEntries = async () => {
    setLoading(true);
    try {
      const headers = {};
      const token = localStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;

      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`${API}/api/scuta/entries${params}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
      }
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  };

  const months = groupByMonth(entries);
  const canViewDrafts = !!user;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0B0F14' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50"
        style={{ backgroundColor: '#0B0F14', borderBottom: '1px solid #1E2936' }}
      >
        <div className="max-w-3xl mx-auto px-4 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/scuta')}
              className="p-2 rounded-full hover:opacity-70"
              data-testid="scuta-entries-back-btn"
            >
              <ArrowLeft className="w-5 h-5" style={{ color: '#E6E9EE' }} />
            </button>
            <div>
              <h1 className="text-lg font-bold tracking-tight" style={{ color: '#E6E9EE' }}>
                Activity Records
              </h1>
              <p className="text-[10px] tracking-widest uppercase" style={{ color: '#9AA3AF' }}>
                SCUTA Archive
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Filter tabs */}
      <div className="max-w-3xl mx-auto px-4 py-3">
        <div className="flex gap-2" data-testid="scuta-entries-filters">
          {[
            { id: 'approved', label: 'Published' },
            ...(canViewDrafts ? [{ id: 'all', label: 'All Mine' }] : []),
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id)}
              className="px-4 py-1.5 rounded-full text-xs font-medium transition-all"
              style={{
                backgroundColor: filter === tab.id ? '#C2A45E' : '#1E2936',
                color: filter === tab.id ? '#0B0F14' : '#9AA3AF',
              }}
              data-testid={`filter-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <main className="max-w-3xl mx-auto px-4 pb-12">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2" style={{ borderColor: '#C2A45E' }} />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16" data-testid="scuta-entries-empty">
            <FileText className="w-10 h-10 mx-auto mb-3" style={{ color: '#1E2936' }} />
            <p className="text-sm" style={{ color: '#9AA3AF' }}>
              {filter === 'approved' ? 'No published records yet.' : 'No entries found.'}
            </p>
            <p className="text-xs mt-1" style={{ color: '#9AA3AF' }}>
              Records appear here when completed activities are archived into SCUTA.
            </p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="scuta-entries-timeline">
            {months.map(month => (
              <div key={month.key}>
                {/* Month header */}
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className="text-xs font-semibold uppercase tracking-widest"
                    style={{ color: '#C2A45E' }}
                  >
                    {month.label}
                  </span>
                  <div className="flex-1 h-px" style={{ backgroundColor: '#1E2936' }} />
                </div>

                {/* Entries */}
                <div className="space-y-3">
                  {month.entries.map(entry => (
                    <EntryCard
                      key={entry.entry_id}
                      entry={entry}
                      isOwner={user?.user_id === entry.host_id}
                      onOpen={() => navigate(`/scuta/entries/${entry.entry_id}`)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function EntryCard({ entry, isOwner, onOpen }) {
  const statusInfo = STATUS_STYLES[entry.status] || STATUS_STYLES.draft;
  const photoCount = (entry.photos || []).length;

  return (
    <Card
      className="overflow-hidden cursor-pointer transition-all hover:opacity-90"
      style={{ backgroundColor: '#111820', border: '1px solid #1E2936' }}
      onClick={onOpen}
      data-testid={`scuta-entry-${entry.entry_id}`}
    >
      <div className="p-4">
        {/* Status + Title */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            {entry.status !== 'approved' && (
              <span
                className="inline-block px-2 py-0.5 rounded text-[10px] font-medium mb-1.5"
                style={{ backgroundColor: `${statusInfo.color}20`, color: statusInfo.color }}
                data-testid="entry-status-badge"
              >
                {statusInfo.label}
              </span>
            )}
            <h3 className="font-semibold text-sm" style={{ color: '#E6E9EE' }}>
              {entry.title}
            </h3>
          </div>
          <ChevronRight className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: '#9AA3AF' }} />
        </div>

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-3 text-[11px]" style={{ color: '#9AA3AF' }}>
          {entry.completion_date && (
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDate(entry.completion_date)}
            </span>
          )}
          {entry.location && (
            <span className="flex items-center gap-1 truncate">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              {entry.location}
            </span>
          )}
          {entry.host_name && (
            <span className="flex items-center gap-1">
              <User className="w-3 h-3" />
              {entry.host_name}
            </span>
          )}
        </div>

        {/* Association badges */}
        <div className="flex flex-wrap gap-2 mt-2">
          {entry.institution_id && (
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: '#1E2936', color: '#9AA3AF' }}>
              <Building className="w-3 h-3" /> Institution
            </span>
          )}
          {entry.commons_id && (
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: '#1E2936', color: '#9AA3AF' }}>
              <Landmark className="w-3 h-3" /> Commons
            </span>
          )}
          {entry.group_id && (
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: '#1E2936', color: '#9AA3AF' }}>
              <Users className="w-3 h-3" /> Group
            </span>
          )}
          {photoCount > 0 && (
            <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded" style={{ backgroundColor: '#1E2936', color: '#9AA3AF' }}>
              <Image className="w-3 h-3" /> {photoCount}
            </span>
          )}
        </div>

        {/* Description preview */}
        {entry.description && (
          <p
            className="text-xs mt-2 line-clamp-2 leading-relaxed"
            style={{ color: '#9AA3AF' }}
          >
            {entry.description}
          </p>
        )}
      </div>
    </Card>
  );
}
